// frontend/src/marketing/seo/schema.ts
//
// Pure builder functions for schema.org JSON-LD structured data.
//
// Structured data is a primary lever for BOTH traditional SEO (rich results,
// knowledge-panel eligibility) AND Answer Engine Optimization (AEO) — LLM
// answer engines (ChatGPT, Perplexity, Google AI Overviews, Gemini) lean on
// explicit machine-readable facts when deciding what to cite. Marking up the
// Organization, the site, each blog post, each help article, the pricing FAQ,
// and the breadcrumb trail gives those engines unambiguous entities to quote.
//
// These functions are intentionally PURE (no DOM, no React, no `window`) so
// they are trivially unit-testable. The impure "read the current origin" and
// "inject into <head>" concerns live in ./JsonLd.tsx.

/** The canonical brand name used across every schema node. */
export const SITE_NAME = 'Worqly';

/** Production origin — used as the fallback when `window` is unavailable. */
export const DEFAULT_ORIGIN = 'https://worqly.io';

/** Path (relative to origin) of the raster logo used for Organization/publisher. */
export const LOGO_PATH = '/logo512.png';

const DEFAULT_DESCRIPTION =
  'Worqly is the modular operating system for small business — invoicing, payroll, expenses, HR, time tracking and more. Pay for the modules you use and the employees you actually track.';

export interface JsonLdNode {
  '@context': 'https://schema.org';
  '@type': string;
  // Structured-data nodes are heterogeneous; consumers read nested schema.org
  // fields (publisher['@type'], mainEntity[…]) so an `any` index keeps call
  // sites and tests ergonomic without a cast at every access.
  [key: string]: any;
}

/**
 * Join an origin and a path into an absolute URL.
 * - Absolute inputs (http/https) pass through unchanged.
 * - The root path ('/') yields the bare origin (no trailing slash) so it
 *   matches the canonical the SPA emits.
 */
export function absUrl(origin: string, pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const cleanOrigin = origin.replace(/\/+$/, '');
  if (!pathOrUrl || pathOrUrl === '/') return cleanOrigin;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${cleanOrigin}${path}`;
}

/** The publisher sub-node reused by article schemas. */
function publisherNode(origin: string) {
  return {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: absUrl(origin, LOGO_PATH),
    },
  };
}

export function buildOrganizationSchema(opts: {
  origin: string;
  name?: string;
  description?: string;
  logoPath?: string;
  sameAs?: string[];
}): JsonLdNode {
  const node: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: opts.name || SITE_NAME,
    url: absUrl(opts.origin, '/'),
    logo: absUrl(opts.origin, opts.logoPath || LOGO_PATH),
    description: opts.description || DEFAULT_DESCRIPTION,
  };
  if (opts.sameAs && opts.sameAs.length > 0) {
    node.sameAs = opts.sameAs;
  }
  return node;
}

export function buildWebSiteSchema(opts: {
  origin: string;
  name?: string;
  description?: string;
}): JsonLdNode {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: opts.name || SITE_NAME,
    url: absUrl(opts.origin, '/'),
    description: opts.description || DEFAULT_DESCRIPTION,
    publisher: {
      '@type': 'Organization',
      name: opts.name || SITE_NAME,
    },
  };
}

export interface BlogPostingInput {
  slug: string;
  title: string;
  excerpt?: string | null;
  author_name?: string | null;
  tags?: string[];
  cover_url?: string | null;
  og_image_url?: string | null;
  published_at?: string | null;
  updated_at?: string | null;
}

export function buildBlogPostingSchema(opts: {
  post: BlogPostingInput;
  origin: string;
}): JsonLdNode {
  const { post, origin } = opts;
  const url = absUrl(origin, `/blog/${post.slug}`);
  const image = post.og_image_url || post.cover_url || null;

  const node: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: post.author_name
      ? { '@type': 'Person', name: post.author_name }
      : { '@type': 'Organization', name: SITE_NAME },
    publisher: publisherNode(origin),
  };

  if (post.excerpt) node.description = post.excerpt;
  if (image) node.image = [absUrl(origin, image)];
  if (post.published_at) {
    node.datePublished = post.published_at;
    node.dateModified = post.updated_at || post.published_at;
  }
  if (post.tags && post.tags.length > 0) node.keywords = post.tags.join(', ');

  return node;
}

export interface TechArticleInput {
  slug: string;
  title: string;
  excerpt?: string | null;
  category?: string | null;
  published_at?: string | null;
  updated_at?: string | null;
}

export function buildTechArticleSchema(opts: {
  doc: TechArticleInput;
  origin: string;
}): JsonLdNode {
  const { doc, origin } = opts;
  const url = absUrl(origin, `/docs/${doc.slug}`);

  const node: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: doc.title,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    publisher: publisherNode(origin),
  };

  if (doc.excerpt) node.description = doc.excerpt;
  if (doc.category) node.articleSection = doc.category;
  if (doc.published_at) {
    node.datePublished = doc.published_at;
    node.dateModified = doc.updated_at || doc.published_at;
  }

  return node;
}

export function buildFaqPageSchema(items: Array<{ q: string; a: string }>): JsonLdNode {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };
}

export function buildBreadcrumbSchema(
  items: Array<{ name: string; path: string }>,
  origin: string,
): JsonLdNode {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: absUrl(origin, it.path),
    })),
  };
}
